import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useIsMobile } from "../../hooks/useIsMobile";
import Case from "./Case";
import { CaseStatus, CaseStatusType, Case as CaseType } from "../../db/schema";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useUserRoles } from "../../utilities/roleUtils";
import {
  TrashIcon,
  MagnifyingGlassIcon,
  XCircleIcon,
  FunnelIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import Input from "../common/Input";
import { dismissCaseToast } from "../../services/audioProcessingService";
import { useCaseCategories } from "../../context/CaseCategoriesContext";

// Cases as returned by the API include per-user category/favorite enrichment.
type CaseListItem = CaseType & {
  categoryIds?: string[];
  isFavorite?: boolean;
  userName?: string;
};

interface CasesProps {
  handleSelectedCase: (caseId: string) => void;
  setCaseContent?: (content: { data: string } | undefined) => void;
  newCase: CaseType | null;
  selectedCaseId: string;
  onCasesUpdate: (casesExist: boolean) => void;
  caseContent?: { data: string };
  caseStatus?: (typeof CaseStatus.enumValues)[number];
  isCaseLoading: boolean;
  page: string;
  discardedCaseId: string | null;
  erroredCase?: { caseId: string; shouldSelectNext: boolean } | null;
}

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

function Cases({
  handleSelectedCase,
  setCaseContent,
  newCase,
  selectedCaseId,
  onCasesUpdate,
  caseContent,
  caseStatus,
  isCaseLoading,
  page,
  discardedCaseId,
  erroredCase,
}: CasesProps) {
  const { user } = useAuth();
  const { isRecords } = useUserRoles();
  const isRecordsUser = isRecords(); // Create stable reference
  const { activeFilter, mutationVersion, categories } = useCaseCategories();
  const [cases, setCases] = useState<CaseListItem[]>([]);
  const [allCases, setAllCases] = useState<CaseListItem[]>([]); // Store unfiltered cases
  // Agency-configurable trash retention window shown in the trash footer.
  // Defaults to 30 (the legacy behavior for individual users).
  const [trashRetentionDays, setTrashRetentionDays] = useState<number>(30);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedOfficer, setSelectedOfficer] = useState<string | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [officers, setOfficers] = useState<Array<{ id: string; name: string }>>(
    []
  );
  const [delayedEmptyMessage, setDelayedEmptyMessage] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [totalCases, setTotalCases] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const isInitialLoad = useRef(true);
  const location = useLocation();
  const prevPageRef = useRef(page);
  // Track the active category filter and how many rows we've loaded so a
  // category/favorite change (or a category mutation) can refetch and reselect.
  const filterKey =
    page === "cases"
      ? `${activeFilter.type}:${activeFilter.categoryId || ""}`
      : "";
  const prevFilterRef = useRef(filterKey);
  const loadedCountRef = useRef(0);
  const isMobile = useIsMobile();
  const wasSearching = useRef(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const PAGE_SIZE = 50;

  // Fetch the agency's trash retention window for the trash footer copy
  useEffect(() => {
    if (page !== "trash") return;
    let cancelled = false;
    axios
      .get("/api/settings/data-retention", { withCredentials: true })
      .then((res) => {
        if (!cancelled && res.data?.trashRetentionDays) {
          setTrashRetentionDays(res.data.trashRetentionDays);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [page]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsFilterOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (debouncedSearchQuery) {
      setDelayedEmptyMessage(true);
      wasSearching.current = true;
    } else {
      // When search is cleared, select the case from URL if it exists
      if (wasSearching.current && cases.length > 0) {
        const caseId = location.pathname.split("/").pop();
        if (caseId && cases.find((c) => c.id === caseId)) {
          handleSelectedCase(caseId);
        }
        wasSearching.current = false;
      }
      const timer = setTimeout(() => {
        setDelayedEmptyMessage(false);
      }, 500);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery]);

  useEffect(() => {
    if (caseContent && selectedCaseId && !isCaseLoading) {
      setCases((prevCases) => {
        // Find the current case
        const currentCase = prevCases.find((c) => c.id === selectedCaseId);

        // Only update and move if the content actually changed
        const contentChanged = currentCase &&
          JSON.stringify(currentCase.content) !== JSON.stringify(caseContent);

        if (!contentChanged) {
          return prevCases; // No change, return as-is
        }

        const updatedCases = prevCases.map((c) =>
          c.id === selectedCaseId
            ? { ...c, content: caseContent, updatedAt: new Date() }
            : c
        );

        // Move the edited case to the top
        const editedCaseIndex = updatedCases.findIndex((c) => c.id === selectedCaseId);
        if (editedCaseIndex > 0) {
          const editedCase = updatedCases[editedCaseIndex];
          updatedCases.splice(editedCaseIndex, 1);
          updatedCases.unshift(editedCase);
        }

        return updatedCases;
      });
    }
  }, [caseContent, selectedCaseId, isCaseLoading]);

  // Update case status in list when it changes (e.g., checklist → processing)
  useEffect(() => {
    if (caseStatus && selectedCaseId && !isCaseLoading) {
      setCases((prevCases) => {
        const currentCase = prevCases.find((c) => c.id === selectedCaseId);

        // Only update if the status actually changed
        if (!currentCase || currentCase.status === caseStatus) {
          return prevCases;
        }

        return prevCases.map((c) =>
          c.id === selectedCaseId
            ? { ...c, status: caseStatus }
            : c
        );
      });
    }
  }, [caseStatus, selectedCaseId, isCaseLoading]);

  const fetchCases = useCallback(
    async (userId?: string, limit = PAGE_SIZE, offset = 0, search?: string) => {
      let status;
      switch (page) {
        case "cases":
          status = [
            CaseStatusType.enum.active,
            CaseStatusType.enum.processing,
            CaseStatusType.enum.checklist
          ];
          break;
        case "trash":
          status = CaseStatusType.enum.discarded;
          break;
        default:
          status = "";
      }

      const params: Record<string, unknown> = {
        userId,
        status,
        limit,
        offset,
        search,
      };
      // Category/favorite filtering only applies to the active Cases view.
      if (page === "cases") {
        if (activeFilter.type === "category" && activeFilter.categoryId) {
          params.categoryId = activeFilter.categoryId;
        } else if (activeFilter.type === "favorites") {
          params.favorite = "true";
        }
      }

      try {
        const response = await axios.get("/api/cases", {
          params,
          withCredentials: true,
        });
        return response.data;
      } catch (error) {
        console.error("Error fetching cases:", error);
        throw error;
      }
    },
    [page, activeFilter]
  );

  const loadCases = useCallback(async () => {
    const isPageChange = prevPageRef.current !== page;
    const isFilterChange = prevFilterRef.current !== filterKey;
    // On a page/filter change we reset to the first page; otherwise (e.g. a
    // category mutation refetch) we re-request however many rows are already
    // loaded so infinite-scroll position and selection are preserved.
    const resetView = isPageChange || isFilterChange || isInitialLoad.current;
    const limit = resetView
      ? PAGE_SIZE
      : Math.max(PAGE_SIZE, loadedCountRef.current);

    try {
      const response = await fetchCases(user?.id, limit, 0, debouncedSearchQuery);
      const data = response.cases;

      // Store all cases and extract unique officers if user has records role
      setAllCases(data);
      loadedCountRef.current = data.length;
      setHasMore(response.hasMore);
      setTotalCases(response.total);
      if (isRecordsUser) {
        const uniqueOfficers = data
          .filter((caseItem: any) => caseItem.userName)
          .reduce((acc: Array<{ id: string; name: string }>, caseItem: any) => {
            if (!acc.find((officer) => officer.id === caseItem.userId)) {
              acc.push({ id: caseItem.userId, name: caseItem.userName });
            }
            return acc;
          }, []);
        setOfficers(uniqueOfficers);
      }

      // Apply filter immediately if one is selected, otherwise show all
      const filteredData = selectedOfficer
        ? data.filter((caseItem: any) => caseItem.userId === selectedOfficer)
        : data;

      setCases(filteredData);
      // Only report "no cases" if there are genuinely no cases (not just filtered by search)
      onCasesUpdate(data.length > 0);

      prevPageRef.current = page;
      prevFilterRef.current = filterKey;

      const isRootPath =
        location.pathname === "/cases" || location.pathname === "/trash";

      // Auto-select the first case on initial load, a new case, a page change,
      // or a category/favorite filter change — but never on a plain mutation
      // refetch (which would yank the user's selection around).
      const shouldAutoSelect =
        ((isInitialLoad.current || newCase) && isRootPath) ||
        isPageChange ||
        isFilterChange;

      if (
        shouldAutoSelect &&
        filteredData.length > 0 &&
        !isMobile &&
        !isCaseLoading
      ) {
        const newestCase = filteredData[0];
        handleSelectedCase(newestCase.id);
        if (setCaseContent) {
          setCaseContent(newestCase.content as { data: string });
        }
        isInitialLoad.current = false;
      } else if (filteredData.length === 0 && !debouncedSearchQuery) {
        // Only clear selected case if there are genuinely no cases (not just filtered by search)
        handleSelectedCase("");
        if (setCaseContent) {
          setCaseContent(undefined);
        }
      }
    } catch (error) {
      console.error("Cases.tsx error:", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user?.id,
    fetchCases,
    newCase,
    isMobile,
    debouncedSearchQuery,
    isRecordsUser,
    selectedOfficer,
    filterKey,
    mutationVersion,
  ]);

  const loadMoreCases = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    try {
      const response = await fetchCases(user?.id, PAGE_SIZE, allCases.length, debouncedSearchQuery);
      const newData = response.cases;

      setAllCases((prev) => [...prev, ...newData]);
      loadedCountRef.current += newData.length;
      setHasMore(response.hasMore);
      setTotalCases(response.total);

      if (isRecordsUser) {
        setOfficers((prev) => {
          const updated = [...prev];
          for (const caseItem of newData) {
            if (caseItem.userName && !updated.find((o: any) => o.id === caseItem.userId)) {
              updated.push({ id: caseItem.userId, name: caseItem.userName });
            }
          }
          return updated;
        });
      }

      // Append to displayed cases (apply officer filter)
      const filteredNew = selectedOfficer
        ? newData.filter((caseItem: any) => caseItem.userId === selectedOfficer)
        : newData;
      setCases((prev) => [...prev, ...filteredNew]);
    } catch (error) {
      console.error("Error loading more cases:", error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore, fetchCases, user?.id, allCases.length, debouncedSearchQuery, isRecordsUser, selectedOfficer]);

  // Separate effect to apply officer filter
  useEffect(() => {
    if (allCases.length > 0) {
      const filteredData = selectedOfficer
        ? allCases.filter(
            (caseItem: any) => caseItem.userId === selectedOfficer
          )
        : allCases;

      setCases(filteredData);
      // Don't call onCasesUpdate here - it's already called in loadCases
      // and should only reflect whether there are ANY cases, not filtered results
    }
  }, [selectedOfficer, allCases]);

  const handleDelete = async () => {
    if (!selectedCaseId) {
      console.error("No case selected for deletion");
      return;
    }

    // Dismiss any processing toast associated with this case
    dismissCaseToast(selectedCaseId);

    try {
      const response = await axios.patch(
        `/api/cases/${selectedCaseId}/discard`,
        {},
        { withCredentials: true }
      );

      if (response.status === 200) {
        console.log("Case discarded successfully:", response.data);

        // Find the index of the deleted case
        const deletedIndex = cases.findIndex((c) => c.id === selectedCaseId);

        // Update local state
        const updatedCases = cases.map((c) =>
          c.id === selectedCaseId
            ? { ...c, status: CaseStatusType.enum.discarded }
            : c
        );

        // Filter out discarded cases
        const activeCases = updatedCases.filter(
          (c) => c.status !== CaseStatusType.enum.discarded
        );

        setCases(activeCases);
        onCasesUpdate(activeCases.length > 0);

        // Select the case at the same index or the nearest available case
        if (activeCases.length > 0) {
          let newSelectedIndex = Math.min(deletedIndex, activeCases.length - 1);
          const newSelectedCase = activeCases[newSelectedIndex];
          handleSelectedCase(newSelectedCase.id);
          if (setCaseContent) {
            setCaseContent(newSelectedCase.content as { data: string });
          }
        } else {
          handleSelectedCase("");
          if (setCaseContent) {
            setCaseContent(undefined);
          }
        }

        // Reload the cases to ensure everything is up-to-date
        // Only reload if we're on the current page and not filtering
        if (!selectedOfficer) {
          loadCases();
        }
      }
    } catch (error) {
      console.error("Error discarding case:", error);
    }
  };

  useEffect(() => {
    loadCases();
  }, [loadCases]);

  // Infinite scroll handler
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      // Load more when within 200px of the bottom
      if (scrollHeight - scrollTop - clientHeight < 200) {
        loadMoreCases();
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [loadMoreCases]);

  // Handle case discarded from upload cancellation
  useEffect(() => {
    if (discardedCaseId) {
      // Find the index of the discarded case in current cases
      const discardedIndex = cases.findIndex((c) => c.id === discardedCaseId);

      // Remove the discarded case from local state
      const updatedCases = cases.filter((c) => c.id !== discardedCaseId);
      setCases(updatedCases);
      onCasesUpdate(updatedCases.length > 0);

      // Select the case at the same index or the nearest available case
      if (updatedCases.length > 0) {
        const newSelectedIndex = discardedIndex >= 0
          ? Math.min(discardedIndex, updatedCases.length - 1)
          : 0;
        const newSelectedCase = updatedCases[newSelectedIndex];
        handleSelectedCase(newSelectedCase.id);
        if (setCaseContent) {
          setCaseContent(newSelectedCase.content as { data: string });
        }
      } else {
        handleSelectedCase("");
        if (setCaseContent) {
          setCaseContent(undefined);
        }
      }
    }
    // We intentionally omit `cases` from dependencies to avoid infinite loop
    // This effect should only run when discardedCaseId changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discardedCaseId]);

  // Handle case error - remove from list and optionally select next
  useEffect(() => {
    if (erroredCase) {
      const { caseId, shouldSelectNext } = erroredCase;

      // Find the index of the errored case in current cases
      const erroredIndex = cases.findIndex((c) => c.id === caseId);

      // Remove the errored case from local state
      const updatedCases = cases.filter((c) => c.id !== caseId);
      setCases(updatedCases);
      onCasesUpdate(updatedCases.length > 0);

      // Only select next case if the errored case was currently selected
      if (shouldSelectNext) {
        if (updatedCases.length > 0) {
          const newSelectedIndex = erroredIndex >= 0
            ? Math.min(erroredIndex, updatedCases.length - 1)
            : 0;
          const newSelectedCase = updatedCases[newSelectedIndex];
          handleSelectedCase(newSelectedCase.id);
          if (setCaseContent) {
            setCaseContent(newSelectedCase.content as { data: string });
          }
        } else {
          handleSelectedCase("");
          if (setCaseContent) {
            setCaseContent(undefined);
          }
        }
      }
    }
    // We intentionally omit `cases` from dependencies to avoid infinite loop
    // This effect should only run when erroredCase changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [erroredCase]);

  // useEffect(() => {
  //   if (newCase) {
  //     loadCases();
  //   }
  // }, [newCase, loadCases]);

  function renderCases() {
    return cases.map((caseItem) => (
      <Case
        key={caseItem.id}
        {...caseItem}
        status={caseItem.status}
        handleSelectedCase={handleSelectedCase}
        selected={caseItem.id === selectedCaseId}
        currentUserId={user?.id}
        isRecordsUser={isRecords()}
        draggable={page === "cases"}
      />
    ));
  }

  // Heading reflects the active category/favorites filter on the Cases view.
  const activeCategory = categories.find(
    (c) => c.id === activeFilter.categoryId
  );
  const headingText =
    page === "cases"
      ? activeFilter.type === "favorites"
        ? "Favorites"
        : activeFilter.type === "category"
          ? activeCategory?.name || "Cases"
          : "Cases"
      : page;

  return (
    <div className="w-full h-screen flex flex-col pt-16 md:pt-6 p-6 pb-0">
      <h2 className="hidden md:block text-3xl font-semibold text-black-400 dark:text-base-content capitalize">
        {headingText}
      </h2>
      <div className="mt-6 md:mt-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <p className="mx-auto pl-5 md:pl-0 md:mx-0 text-black-400 dark:text-base-content font-bold md:font-normal text-sm 2xl:text-base">
            {totalCases} {isRecords() ? "agency " : ""}cases
          </p>
          {isRecords() && officers.length > 0 && (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 dark:bg-base-200 hover:bg-base-300 rounded-md border transition-colors"
              >
                <FunnelIcon className="w-3 h-3" />
                {selectedOfficer
                  ? officers.find((o) => o.id === selectedOfficer)?.name ||
                    "All officers"
                  : "All officers"}
                <ChevronDownIcon
                  className={`w-3 h-3 transition-transform ${
                    isFilterOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {isFilterOpen && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-base-100 border border-base-300 rounded-md shadow-lg z-10 max-h-40 overflow-y-auto">
                  <button
                    onClick={() => {
                      setSelectedOfficer(null);
                      setIsFilterOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-base-200 ${
                      !selectedOfficer ? "bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300" : ""
                    }`}
                  >
                    All Officers
                  </button>
                  {officers.map((officer) => (
                    <button
                      key={officer.id}
                      onClick={() => {
                        setSelectedOfficer(officer.id);
                        setIsFilterOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-base-200 ${
                        selectedOfficer === officer.id
                          ? "bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300"
                          : ""
                      }`}
                    >
                      {officer.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        {page === "cases" && (
          <button
            data-tour="delete-case"
            onClick={handleDelete}
            className="btn pl-0 pr-0 border-0 min-h-0 h-auto focus-visible:border-0 shadow-none"
            disabled={
              cases.length === 0 ||
              (!!selectedCaseId &&
                cases.find((c) => c.id === selectedCaseId)?.userId !== user?.id)
            }
          >
            <TrashIcon
              className={`size-5 mr-1 transition-all duration-300 ease-in-out ${
                cases.length > 0 &&
                (!selectedCaseId ||
                  cases.find((c) => c.id === selectedCaseId)?.userId ===
                    user?.id)
                  ? "text-blue-500 hover:text-blue-700 dark:text-blue-300 dark:hover:text-blue-200 cursor-pointer"
                  : "cursor-not-allowed text-base-content/40"
              }`}
            />
          </button>
        )}
      </div>
      <div className="mt-4 relative" data-tour="cases-search">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-base-content/40" />
        <Input
          type="text"
          placeholder="Search cases..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="p-2 pl-10 w-full focus:border-blue-400"
          variant="sm"
        />
        {searchQuery && (
          <XCircleIcon
            className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-base-content/40 hover:text-gray-600 dark:hover:text-base-content/70 cursor-pointer"
            onClick={() => setSearchQuery("")}
          />
        )}
      </div>
      {page === "trash" && (
        <p data-tour="trash-banner" className="mt-3 text-xs text-base-content/40 text-center">
          Cases older than {trashRetentionDays} days are automatically deleted
        </p>
      )}
      <div ref={scrollContainerRef} className="mt-2 overflow-y-auto scrollbar-hide border-t-2 border-base-200 md:border-t-0">
        {cases.length > 0 ? (
          <>
            {renderCases()}
            {isLoadingMore && (
              <div className="flex justify-center py-4">
                <span className="loading loading-spinner loading-sm text-base-content/40"></span>
              </div>
            )}
          </>
        ) : (
          <p className="pt-4 md:mt-0 text-base-content/60 text-center">
            {delayedEmptyMessage ? "No matching cases" : "No cases available"}
          </p>
        )}
      </div>
    </div>
  );
}

export default Cases;
